import useEmblaCarousel from 'embla-carousel-react'
import { useIntersectionObserver } from 'hooks/useIntersectionObserver'
import { useRef } from 'react'
import { DEFAULT_OPTIONS, InnerCarousel } from 'components/Carousel/Carousel'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useVisualTemplates } from 'hooks/useVisualTemplates'
import { collectResults } from 'utils/query'
import { Heading } from 'blaze-ui/Heading/Heading'
import Styles from './TemplateSelect.module.scss'
import { PreviousButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/PreviousButton'
import { NextButton } from 'screens/blaze/Home/components/Catalogue/components/ScrollButton/NextButton'
import { ShimmeringColor } from 'components/BrandWizardVisualAnalysisModal/LoadingState'
import { ITEM_HEIGHT, ITEM_WIDTH } from 'components/Wizards/Wizard/Form/TemplatesSelect/TemplatesSelect'
import { TemplatePreview } from 'components/Wizards/Wizard/Form/TemplateSelect/TemplatePreview'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import cx from 'classnames'

interface Props {
  onPress: (templateId: number) => void
  width: number
  height: number
}

export const Newest = ({ onPress, width, height }: Props) => {
  const ITEMS_TO_LOAD = 10
  const elementRef = useRef<HTMLDivElement>(null)
  const intersection = useIntersectionObserver(elementRef, { threshold: [0, 0.00000001] })
  const isVisible = (intersection?.intersectionRatio ?? 0) >= 0.00000001
  const embla = useEmblaCarousel({
    ...DEFAULT_OPTIONS,
    dragFree: true,
    align: 'end',
    slidesToScroll: 'auto',
  })

  const emblaApi = embla[1]
  const { isMobile } = useResponsiveDesign()

  const query = useVisualTemplates(
    {
      element_type: 'Template',
      items: ITEMS_TO_LOAD,
      order_by: 'random',
      width,
      height,
    },
    {
      enabled: isVisible,
      staleTime: Infinity,
    },
  )

  const templates = collectResults(query, (data) => data.visual_elements)
  const { fetchNextPage, hasNextPage } = query

  return (
    <div ref={elementRef} className={cx(Styles.category, Styles.newest)}>
      <div className={Styles.title} style={{ paddingLeft: 30 }}>
        <Heading level={3}>Check out our newest templates</Heading>
        {emblaApi && templates.length > 1 && !isMobile && (
          <div className={Styles.controls}>
            <PreviousButton emblaApi={emblaApi} />
            <NextButton emblaApi={emblaApi} />
          </div>
        )}
      </div>

      <div className={Styles.templatesCarousel}>
        <InnerCarousel embla={embla} withControls={false} offset={30}>
          {(!query.isFetched || query.isLoading) &&
            Array.from({ length: 10 }).map((_, i) => (
              <ShimmeringColor aspectRatio={1} width={ITEM_WIDTH} height={ITEM_HEIGHT} key={i} />
            ))}
          {!query.isLoading &&
            templates.map((template, i) => {
              return (
                <TemplatePreview
                  key={`${template.id}-${i}`}
                  template={template}
                  onPress={() => {
                    onPress(template.id)
                  }}
                  isSelected={false}
                  aspectRatio={template.width / template.height}
                  itemWidth={ITEM_WIDTH}
                  itemHeight={ITEM_HEIGHT}
                  largePreview={true}
                />
              )
            })}
          {hasNextPage && (
            <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage}>
              <ShimmeringColor aspectRatio={1} width={ITEM_WIDTH} height={ITEM_HEIGHT} />
            </InfiniteScroll>
          )}
          {hasNextPage &&
            Array.from({ length: 2 }).map((_, i) => (
              <ShimmeringColor key={i} aspectRatio={1} width={ITEM_WIDTH} height={ITEM_HEIGHT} />
            ))}
        </InnerCarousel>
      </div>
    </div>
  )
}
